import rehypeParse from "rehype-parse";
import rehypeRemark from "rehype-remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import remarkParse from "remark-parse";
import remarkStringify from "remark-stringify";
import { unified } from "unified";
import { selectAll } from "hast-util-select";
import { h } from "hastscript";
import type { Root } from "hast";
import { toHtml } from "hast-util-to-html";
import type { Options } from "hast-util-to-mdast";
import { Html } from "mdast";

export function markdownFromHTML(html: string): string {
  // Pre-process the HTML to replace tags with placeholders
  let processedHtml = html;

  // Handle empty paragraphs by replacing <p></p> with <p>&nbsp;</p> to preserve them in markdown.
  // Issue: https://gitlab.sys.mixxt.net/tixxt/foundation/-/issues/4094#note_114006
  processedHtml = processedHtml.replace(/<p>\s*<\/p>/g, "<p>&nbsp;</p>");

  // Store color spans to re-apply after markdown conversion
  const colorSpans: Array<{
    color: string;
  }> = [];
  let colorSpanIndex = 0;

  // Replace colored span tags - extract content but keep a placeholder
  // We'll let the content be processed as regular HTML, then re-wrap it
  // Use HTML comment-style placeholders that won't be escaped
  processedHtml = processedHtml.replace(
    /<span style="color: ([\s\S]*?)">([\s\S]*?)<\/span>/g,
    (_, color, content) => {
      const placeholder = `<!--COLORSPAN${colorSpanIndex}START-->`;
      const endPlaceholder = `<!--COLORSPAN${colorSpanIndex}END-->`;
      colorSpans.push({
        color,
      });
      colorSpanIndex++;
      // Return the content with markers around it so it gets processed as markdown
      return `${placeholder}${content}${endPlaceholder}`;
    },
  );

  // Replace member mention span tags with placeholders
  processedHtml = processedHtml.replace(
    /<span data-id="@\[([\s\S]*?):([\s\S]*?)\]" data-mention="member">([\s\S]*?)<\/span>/g,
    (_, id, name, content) =>
      `<!--MEMBER_MENTION_START:${id}:${name}-->${content}<!--MEMBER_MENTION_END-->`,
  );

  // Replace tag mention span tags with placeholders
  processedHtml = processedHtml.replace(
    /<span data-id="#\[([\s\S]*?):([\s\S]*?)\]" data-mention="tag">([\s\S]*?)<\/span>/g,
    (_, id, displayValue, content) =>
      `<!--TAG_MENTION_START:${id}:${displayValue}-->${content}<!--TAG_MENTION_END-->`,
  );

  const marker = `marker${new Date().getTime()}`;

  const preserve = (state, node) => {
    const result: Html = { type: "html", value: toHtml(node) };
    state.patch(node, result);
    return result;
  };

  // Convert to markdown
  let markdown = unified()
    .use(rehypeParse, { fragment: true })
    .use(() => (tree: Root) => {
      const listItems = selectAll(
        'li[data-list-kind="ordered"][data-list-order]',
        tree,
      );
      listItems.forEach((listItem) => {
        listItem.children.unshift(
          h("span", `${marker}/${listItem.properties.dataListOrder}`),
        );
      });
      return tree;
    })
    .use(rehypeRemark, {
      handlers: {
        u: preserve,
        s: preserve,
        mark: preserve,
        br: preserve, // Preserve <br> tags as HTML to prevent paragraph breaks
      },
    } as Options)
    .use(remarkGfm)
    .use(remarkStringify)
    .processSync(processedHtml)
    .toString();

  // Re-wrap color spans around their content (which has now been converted to markdown)
  colorSpans.forEach(({ color }, index) => {
    const startPlaceholder = `<!--COLORSPAN${index}START-->`;
    const endPlaceholder = `<!--COLORSPAN${index}END-->`;

    // Use a regex to find content between the placeholders
    const regex = new RegExp(
      `${startPlaceholder.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      )}([\\s\\S]*?)${endPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
      "g",
    );

    markdown = markdown.replace(regex, (_, content) => {
      return `<span style="color: ${color}">${content}</span>`;
    });
  });

  // Replace member mention placeholders with @[id:name] format
  markdown = markdown.replace(
    /<!--MEMBER_MENTION_START:([\s\S]*?):([\s\S]*?)-->([\s\S]*?)<!--MEMBER_MENTION_END-->/g,
    (_, id, name) => `@[${id}:${name}]`,
  );

  // Replace tag mention placeholders with #[id:displayValue] format
  markdown = markdown.replace(
    /<!--TAG_MENTION_START:([\s\S]*?):([\s\S]*?)-->([\s\S]*?)<!--TAG_MENTION_END-->/g,
    (_, id, displayValue) => `#[${id}:${displayValue}]`,
  );

  // Replace the markers with the correct list numbers
  markdown = markdown.replace(
    new RegExp(`(\\d+)\\. ${marker}/(\\d+)\\n\\n *`, "g"),
    (_, _currentNumber, originalNumber) => `${originalNumber}. `,
  );

  // Remove any escaped @ symbols that might have been added.
  // Fixes issue with email address - that have a @ symbol in between text - having the @ escaped (with `\\@`).
  markdown = markdown.replace(/\\@/g, "@");

  // Unescape GFM autolink-prevention escapes added by remarkStringify.
  // remarkGfm causes remarkStringify to escape `https\:` and `www\.` to prevent
  // bare URLs from becoming GFM autolinks. This breaks URL detection for link
  // previews and stores unnecessary escapes. Fixes #4737
  markdown = markdown.replace(/(https?)\\:/g, "$1:");
  markdown = markdown.replace(/www\\\./g, "www.");
  markdown = markdown.replace(/(https?:\/\/[^\s]*?)\\&/g, "$1&");

  // Unescape parentheses in markdown link/image destinations.
  // remark-stringify escapes ( and ) inside URLs, but Redcarpet handles balanced parens natively.
  // Without this, URLs like .../P10_QE(2026)000877_EN.pdf get stored as .../P10_QE\(2026\)000877_EN.pdf
  // Fixes #4642
  markdown = markdown.replace(
    /\[([^\]]*)\]\(((?:[^\\)]|\\.)*)\)/g,
    (_, text, url) => `[${text}](${url.replace(/\\([()])/g, "$1")})`,
  );

  return markdown;
}

export function htmlFromMarkdown(markdown: string): string {
  // Pre-process markdown to handle @[id:name] mentions and color spans
  let processedMarkdown = markdown;

  // Color spans don't need special processing - the markdown parser
  // supports markdown syntax inside inline HTML elements, so we can
  // keep the color spans as-is and let markdown inside them be processed

  // Replace @[id:name] mentions with HTML spans
  processedMarkdown = processedMarkdown.replace(
    /@\[(.*?):(.*?)\]/g,
    (_, id, name) =>
      `<span data-id="@[${id}:${name}]" data-mention="member">@${name}</span>`,
  );

  // Replace #[id:displayValue] tag mentions with HTML spans
  processedMarkdown = processedMarkdown.replace(
    /#\[(.*?):(.*?)\]/g,
    (_, id, displayValue) =>
      `<span data-id="#[${id}:${displayValue}]" data-mention="tag">#${displayValue}</span>`,
  );

  processedMarkdown = processedMarkdown.replace(
    /<u>([\s\S]*?)<\/u>/g,
    "<temp-underline>$1</temp-underline>",
  );

  // Convert markdown to HTML
  let html = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkHtml, {
      // Preserve raw HTML in the output
      allowDangerousHtml: true,
      sanitize: false,
    })
    .processSync(processedMarkdown)
    .toString();

  // Replace <del> tags with <s> tags for strikethrough
  html = html.replace(/<del>([\s\S]*?)<\/del>/g, "<s>$1</s>");

  html = html.replace(
    /<temp-underline>([\s\S]*?)<\/temp-underline>/g,
    "<u>$1</u>",
  );

  return html;
}
